import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { CartProvider } from './contexts/CartContext.tsx';
import Navbar from './components/Navbar.tsx';
import Footer from './components/Footer.tsx';
import SuccessBanner from './components/SuccessBanner.tsx';
import { visitorAPI } from './services/api.ts';
import Home from './pages/Home.tsx';
import Products from './pages/Products.tsx';
import ProductDetail from './pages/ProductDetail.tsx';
import Cart from './pages/Cart.tsx';
import Checkout from './pages/Checkout.tsx';
import Orders from './pages/Orders.tsx';
import OrderDetail from './pages/OrderDetail.tsx';
import OrderSuccess from './pages/OrderSuccess.tsx';
import Login from './pages/Login.tsx';
import Register from './pages/Register.tsx';
import Profile from './pages/Profile.tsx';
import Contact from './pages/Contact.tsx';
import ForgotPassword from './pages/ForgotPassword.tsx';
import ResetPassword from './pages/ResetPassword.tsx';
import AdminLogin from './pages/admin/AdminLogin.tsx';
import AdminDashboard from './pages/admin/AdminDashboard.tsx';
import AdminOrders from './pages/admin/AdminOrders.tsx';
import AdminProducts from './pages/admin/AdminProducts.tsx';
import AdminUsers from './pages/admin/AdminUsers.tsx';
import AdminCategories from './pages/admin/AdminCategories.tsx';
import AdminContacts from './pages/admin/AdminContacts.tsx';
import CustomerRoute from './components/CustomerRoute.tsx';
import AdminRoute from './components/AdminRoute.tsx';

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isAdmin, showSuccessBanner, setShowSuccessBanner, successMessage, user } = useAuth();

  // Track visitor on app load
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        // Generate or get unique visitor ID from localStorage
        let uniqueId = localStorage.getItem('h2go_visitor_id');
        if (!uniqueId) {
          uniqueId = 'visitor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
          localStorage.setItem('h2go_visitor_id', uniqueId);
        }

        await visitorAPI.incrementVisitor({
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer || undefined,
          page: window.location.pathname,
          uniqueId: uniqueId
        });
      } catch (error) {
        console.error('Failed to track visitor:', error);
      }
    };

    trackVisitor();
  }, []);

  // Debug logging
  React.useEffect(() => {
    console.log('AppRoutes - showSuccessBanner:', showSuccessBanner);
  }, [showSuccessBanner]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {showSuccessBanner && (
          <SuccessBanner 
            message={successMessage}
            user={user}
            type="welcome"
            onClose={() => {
              console.log('SuccessBanner closing');
              setShowSuccessBanner(false);
            }} 
          />
        )}
        <Navbar />
        <main className="flex-grow">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={
              !isAuthenticated ? <Login /> : 
              isAdmin ? <Navigate to="/admin" /> : <Navigate to="/" />
            } />
            <Route path="/register" element={
              !isAuthenticated ? <Register /> : 
              isAdmin ? <Navigate to="/admin" /> : <Navigate to="/" />
            } />
            <Route path="/forgot-password" element={
              !isAuthenticated ? <ForgotPassword /> : 
              isAdmin ? <Navigate to="/admin" /> : <Navigate to="/" />
            } />
            <Route path="/reset-password" element={
              !isAuthenticated ? <ResetPassword /> : 
              isAdmin ? <Navigate to="/admin" /> : <Navigate to="/" />
            } />
            
            {/* Customer-only routes */}
            <Route path="/cart" element={
              <CustomerRoute>
                <Cart />
              </CustomerRoute>
            } />
            <Route path="/checkout" element={
              <CustomerRoute>
                <Checkout />
              </CustomerRoute>
            } />
            <Route path="/order-success" element={
              <CustomerRoute>
                <OrderSuccess />
              </CustomerRoute>
            } />
            <Route path="/orders" element={
              <CustomerRoute>
                <Orders />
              </CustomerRoute>
            } />
            <Route path="/orders/:id" element={
              <CustomerRoute>
                <OrderDetail />
              </CustomerRoute>
            } />
            <Route path="/profile" element={
              <CustomerRoute>
                <Profile />
              </CustomerRoute>
            } />
            
            {/* Admin routes */}
            <Route path="/admin/login" element={
              !isAuthenticated ? <AdminLogin /> : 
              isAdmin ? <Navigate to="/admin" /> : <Navigate to="/" />
            } />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/orders" element={
              <AdminRoute>
                <AdminOrders />
              </AdminRoute>
            } />
            <Route path="/admin/orders/:id" element={
              <AdminRoute>
                <OrderDetail />
              </AdminRoute>
            } />
            <Route path="/admin/products" element={
              <AdminRoute>
                <AdminProducts />
              </AdminRoute>
            } />
        <Route path="/admin/users" element={
          <AdminRoute>
            <AdminUsers />
          </AdminRoute>
        } />
        <Route path="/admin/categories" element={
          <AdminRoute>
            <AdminCategories />
          </AdminRoute>
        } />
        <Route path="/admin/contacts" element={
          <AdminRoute>
            <AdminContacts />
          </AdminRoute>
        } />
            
            {/* 404 route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <CartProvider>
        <AppRoutes />
      </CartProvider>
    </AuthProvider>
  );
};

export default App;
